/**
 * Calibration Engine — Prompt.md §11.
 *
 * Flow: `COUNTDOWN` (5s) → `COLLECTING` (5s) → lọc nhiễu → `DONE` (có baseline) hoặc `FAILED`
 * (có `failureReason`).
 *
 * **Không có `setTimeout` trong file này.** Mọi chuyển phase suy ra từ `vision.timestamp`
 * (quyết định #8). Nhờ vậy toàn bộ engine test được deterministic, replay được từ debug log, và
 * không bị lệch khi trình duyệt throttle timer ở tab nền — một phiên calibrate bị throttle giữa
 * chừng sẽ cho baseline dựng từ 2 giây dữ liệu mà không ai biết.
 */

import type { AppConfig } from "@studystraight/shared-config";
import type {
  CalibrationFailureReason,
  CalibrationPhase,
  CalibrationSnapshot,
  CameraPlacement,
  MeasurementKey,
  Metric,
  PostureBaseline,
  PostureMeasurement,
  PostureMetricKey,
  VisionResult,
} from "@studystraight/shared-types";
import {
  MEASUREMENT_KEYS,
  METRIC_SOURCES,
  assessDetectionQuality,
  createMeasurer,
  type PlacementMeasurer,
} from "@studystraight/vision-core";

import { summarizeDimension, type DimensionSummary } from "./outlierRemoval.js";

/**
 * Chiều đo tính bằng **độ** — chỉ những chiều này mới so được với `maxBaselineSpreadDeg`.
 * `faceScale`/`shoulderWidth`/`headPosition` là đại lượng tỉ lệ không đơn vị; đem so với ngưỡng
 * 6 độ sẽ cho kết quả vô nghĩa theo cả hai hướng.
 */
const ANGLE_KEYS: readonly MeasurementKey[] = ["headAngle", "torsoAngle", "shoulderTilt"];

/**
 * Chiều đo mà thiếu nó thì baseline không dùng được ở placement tương ứng — nói cách khác,
 * bộ tối thiểu để phát hiện được ít nhất head-down và too-close (§13).
 */
const ESSENTIAL_KEYS: Record<CameraPlacement, readonly MeasurementKey[]> = {
  FRONTAL: ["headPosition", "faceScale"],
  // Mode SIDE bắt buộc thấy hông (REQUIRED_LANDMARKS): không có torsoAngle thì mode này
  // không mang lại gì hơn FRONTAL, nên thất bại thẳng còn tốt hơn cho baseline nửa vời.
  SIDE: ["headAngle", "torsoAngle", "faceScale"],
};

/** Quá bao nhiêu phần sample nghi sai placement thì coi là chọn sai mode (quyết định #21). */
const MISMATCH_FAIL_RATIO = 0.5;

/** Số liệu chi tiết của đợt calibrate — cho Debug Mode (§25) và cho việc tune ngưỡng ở §14. */
export interface CalibrationDiagnostics {
  placement: CameraPlacement;
  framesSeen: number;
  framesAccepted: number;
  mismatchFrames: number;
  summaries: readonly DimensionSummary[];
}

export interface CalibrationEngineOptions {
  config: AppConfig;
  /** Cho phép inject measurer trong test; mặc định suy ra từ `config.placement`. */
  measurer?: PlacementMeasurer;
}

const IDLE_SNAPSHOT: CalibrationSnapshot = {
  phase: "IDLE",
  remainingMs: 0,
  samplesCollected: 0,
  acceptedRatio: 0,
};

/**
 * Nhận `AppConfig` thay vì `(CalibrationConfig, CameraPlacement)` như bản nháp của plan:
 * engine cần cả `posture.minFrameConfidence` (§16) và `posture.minLandmarkVisibility` để đo, nên
 * truyền rời sẽ thành ba nguồn sự thật có thể lệch nhau — và một baseline calibrate ở placement
 * khác với placement đang chạy là failure mode âm thầm nhất của Option C.
 */
export class CalibrationEngine {
  private readonly config: AppConfig;
  private readonly measurer: PlacementMeasurer;

  private phase: CalibrationPhase = "IDLE";
  private startedAt = 0;
  private lastTimestamp = 0;
  private framesSeen = 0;
  private mismatchFrames = 0;
  private samples: PostureMeasurement[] = [];
  /** Kết quả cuối, tính đúng một lần: finalize là O(n log n) và push vẫn được gọi mỗi frame. */
  private outcome: CalibrationSnapshot | null = null;
  private lastDiagnostics: CalibrationDiagnostics | null = null;

  constructor(options: CalibrationEngineOptions) {
    this.config = options.config;
    this.measurer = options.measurer ?? createMeasurer(options.config.placement);
  }

  get placement(): CameraPlacement {
    return this.config.placement;
  }

  /** `now` = ms cùng hệ quy chiếu với `VisionResult.timestamp`. */
  start(now: number): void {
    this.reset();
    this.phase = "COUNTDOWN";
    this.startedAt = now;
    this.lastTimestamp = now;
  }

  reset(): void {
    this.phase = "IDLE";
    this.startedAt = 0;
    this.lastTimestamp = 0;
    this.framesSeen = 0;
    this.mismatchFrames = 0;
    this.samples = [];
    this.outcome = null;
    this.lastDiagnostics = null;
  }

  diagnostics(): CalibrationDiagnostics | null {
    return this.lastDiagnostics;
  }

  /** Gọi mỗi frame. Engine tự chuyển phase dựa trên `vision.timestamp`. */
  push(vision: VisionResult): CalibrationSnapshot {
    if (this.phase === "IDLE") return IDLE_SNAPSHOT;
    if (this.outcome) return this.outcome;

    // Timestamp lùi (đổi camera, video seek) không được tính là thời gian đã trôi.
    this.lastTimestamp = Math.max(this.lastTimestamp, vision.timestamp);
    const elapsed = this.lastTimestamp - this.startedAt;
    const { countdownMs, collectMs } = this.config.calibration;

    if (elapsed < countdownMs) {
      this.phase = "COUNTDOWN";
      return this.snapshot("COUNTDOWN", countdownMs - elapsed);
    }

    if (elapsed < countdownMs + collectMs) {
      this.phase = "COLLECTING";
      this.collect(vision);
      return this.snapshot("COLLECTING", countdownMs + collectMs - elapsed);
    }

    this.outcome = this.finalize();
    this.phase = this.outcome.phase;
    return this.outcome;
  }

  private collect(vision: VisionResult): void {
    this.framesSeen += 1;

    // §16: frame confidence thấp bị bỏ TRƯỚC khi đo. Pose engine vẫn trả toạ độ suy đoán cho
    // landmark bị che, nên đo rồi lọc sau đồng nghĩa với đưa số rác vào trung vị.
    if (vision.confidence < this.config.posture.minFrameConfidence) return;

    const quality = assessDetectionQuality(vision, this.config);
    if (quality.placementMismatch) this.mismatchFrames += 1;

    this.samples.push(this.measurer.measure(vision, this.config.posture));
  }

  private snapshot(phase: CalibrationPhase, remainingMs: number): CalibrationSnapshot {
    return {
      phase,
      remainingMs: Math.max(0, remainingMs),
      samplesCollected: this.samples.length,
      acceptedRatio: this.framesSeen === 0 ? 0 : this.samples.length / this.framesSeen,
    };
  }

  private finalize(): CalibrationSnapshot {
    const { calibration } = this.config;
    const placement = this.config.placement;
    const acceptedRatio = this.framesSeen === 0 ? 0 : this.samples.length / this.framesSeen;

    const summaries = MEASUREMENT_KEYS.map((key) =>
      summarizeDimension(key, this.samples.map((s) => s[key]) as readonly Metric[], calibration),
    );
    this.lastDiagnostics = {
      placement,
      framesSeen: this.framesSeen,
      framesAccepted: this.samples.length,
      mismatchFrames: this.mismatchFrames,
      summaries,
    };

    const failure = this.detectFailure(summaries, acceptedRatio);
    if (failure) {
      return {
        phase: "FAILED",
        remainingMs: 0,
        samplesCollected: this.samples.length,
        acceptedRatio,
        failureReason: failure,
      };
    }

    return {
      phase: "DONE",
      remainingMs: 0,
      samplesCollected: this.samples.length,
      acceptedRatio,
      baseline: this.buildBaseline(summaries),
    };
  }

  /**
   * Thứ tự kiểm tra = thứ tự hữu ích cho người dùng, không phải thứ tự dễ code:
   * không có dữ liệu → chọn sai mode → thiếu landmark → ngồi không yên. Mỗi bước sau chỉ có
   * nghĩa khi bước trước đã loại trừ.
   */
  private detectFailure(
    summaries: readonly DimensionSummary[],
    acceptedRatio: number,
  ): CalibrationFailureReason | null {
    const { calibration } = this.config;

    if (
      this.samples.length < calibration.minSamples ||
      acceptedRatio < calibration.minAcceptedRatio
    ) {
      return "NOT_ENOUGH_SAMPLES";
    }

    if (this.mismatchFrames / this.samples.length > MISMATCH_FAIL_RATIO) {
      return "PLACEMENT_MISMATCH";
    }

    const essential = ESSENTIAL_KEYS[this.config.placement];
    const byKey = new Map(summaries.map((s) => [s.key, s]));
    if (essential.some((key) => byKey.get(key)?.available !== true)) {
      return "LANDMARKS_MISSING";
    }

    const unstable = summaries.some(
      (s) =>
        s.available && ANGLE_KEYS.includes(s.key) && s.spread > calibration.maxBaselineSpreadDeg,
    );
    return unstable ? "TOO_UNSTABLE" : null;
  }

  private buildBaseline(summaries: readonly DimensionSummary[]): PostureBaseline {
    const byKey = new Map(summaries.map((s) => [s.key, s]));
    const centerOf = (key: MeasurementKey): number => {
      const summary = byKey.get(key);
      // Chiều không đủ `available` trả `NaN`, KHÔNG trả 0: 0 sẽ được đọc là "một giá trị baseline
      // hợp lệ" và mọi delta so với nó đều sai. `NaN` lan ra `Metric.available = false`, và
      // `unavailableMetrics` dưới đây là bản ghi chính thức của việc đó.
      return summary?.available === true ? summary.center : Number.NaN;
    };

    const sources = METRIC_SOURCES[this.config.placement];
    const unavailableMetrics = (Object.keys(sources) as PostureMetricKey[]).filter((metricKey) => {
      const keys = sources[metricKey];
      return keys.length === 0 || keys.some((key) => byKey.get(key)?.available !== true);
    });

    return {
      placement: this.config.placement,
      headAngle: centerOf("headAngle"),
      torsoAngle: centerOf("torsoAngle"),
      shoulderTilt: centerOf("shoulderTilt"),
      faceScale: centerOf("faceScale"),
      shoulderWidth: centerOf("shoulderWidth"),
      headPosition: centerOf("headPosition"),
      calibratedAt: this.lastTimestamp,
      unavailableMetrics,
    };
  }
}
